import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { SonarQubeApi } from './sonarqube-api';
import { problemDetailsMessage } from './problem-details';

interface ICreateSonarQubeClient {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

export const createSonarQubeClient = ({
  discoveryApi,
  fetchApi,
}: ICreateSonarQubeClient): SonarQubeApi => {
  // Ids are GUIDs, so they're path-safe (see product-teams' api.ts on why that matters behind Front Door).
  const serviceGroupPath = (serviceGroupId: string) =>
    `/v1/service-groups/${encodeURIComponent(serviceGroupId)}`;

  const send = async (
    method: string,
    path: string,
    body?: unknown,
  ): Promise<Response> =>
    fetchApi.fetch(`${await discoveryApi.getBaseUrl('sonarqube')}${path}`, {
      method,
      ...(body === undefined
        ? {}
        : {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
    });

  const ensureOk = async (response: Response): Promise<Response> => {
    if (!response.ok) {
      throw new Error(await problemDetailsMessage(response));
    }
    return response;
  };

  const get = async (path: string) =>
    (await ensureOk(await send('GET', path))).json();

  const post = async (path: string, body?: unknown): Promise<Response> =>
    ensureOk(await send('POST', path, body));

  const patch = async (path: string, body: unknown): Promise<Response> =>
    ensureOk(await send('PATCH', path, body));

  const del = async (path: string): Promise<Response> =>
    ensureOk(await send('DELETE', path));

  // `undefined` on 404 instead of throwing.
  const getOrUndefined = async (path: string) => {
    const response = await send('GET', path);
    if (response.status === 404) {
      return undefined;
    }
    return (await ensureOk(response)).json();
  };

  return {
    async createServiceGroup(request) {
      const res = await post('/v1/service-groups', request);
      const data = await res.json();
      // Should probably add some central logging so the data can be logged
      return data;
    },
    getServiceGroup: serviceGroupId =>
      getOrUndefined(serviceGroupPath(serviceGroupId)),
    getMembership: serviceGroupId =>
      getOrUndefined(`${serviceGroupPath(serviceGroupId)}/user`),
    async updateServiceGroup(serviceGroupId, request) {
      await patch(serviceGroupPath(serviceGroupId), request);
    },
    async createProject(serviceGroupId, request) {
      await post(`${serviceGroupPath(serviceGroupId)}/projects`, request);
    },
    async updateProject(serviceGroupId, projectKey, request) {
      await patch(
        `${serviceGroupPath(serviceGroupId)}/projects/${encodeURIComponent(
          projectKey,
        )}`,
        request,
      );
    },
    getProjects: serviceGroupId =>
      get(`${serviceGroupPath(serviceGroupId)}/projects`),
    getProjectStatuses: serviceGroupId =>
      get(`${serviceGroupPath(serviceGroupId)}/projects/status`),
    getProjectBindings: serviceGroupId =>
      get(`${serviceGroupPath(serviceGroupId)}/projects/binding`),
    getProjectDescriptions: serviceGroupId =>
      get(`${serviceGroupPath(serviceGroupId)}/projects/description`),
    getProjectQualityGates: serviceGroupId =>
      get(`${serviceGroupPath(serviceGroupId)}/projects/quality-gate`),
    getGroups: serviceGroupId => get(`${serviceGroupPath(serviceGroupId)}/groups`),
    async addUserToGroup(serviceGroupId, role, entraId) {
      await post(
        `${serviceGroupPath(serviceGroupId)}/groups/${encodeURIComponent(
          role,
        )}/users/${encodeURIComponent(entraId)}`,
      );
    },
    async removeUserFromServiceGroup(serviceGroupId, entraId) {
      await del(
        `${serviceGroupPath(serviceGroupId)}/users/${encodeURIComponent(
          entraId,
        )}`,
      );
    },
    async deleteServiceGroup(serviceGroupId) {
      await del(serviceGroupPath(serviceGroupId));
    },
    listQualityGates: () => get('/v1/quality-gates'),
  };
};
